// shadcn-ui v2 New York preset; right-anchored Sheet variant — mirror dialog.tsx file shape
import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils';

type SheetContentEl = React.ElementRef<typeof DialogPrimitive.Content>;

/** How far (px) or how fast (px/ms) a downward release must travel to count
 *  as "let go" rather than "changed their mind" — see {@link useSwipeToDismiss}. */
const SWIPE_DISMISS_DISTANCE = 120;
const SWIPE_DISMISS_VELOCITY = 0.5;
/** Movement (px) before a still-undecided pointer commits to drag-to-dismiss
 *  vs. leaving the gesture to the sheet's own scroller. Small enough that a
 *  real swipe isn't laggy, large enough that a tap's jitter never fires it. */
const SWIPE_LOCK_THRESHOLD = 10;
/** Spring-back duration — also the timer that clears the transition class
 *  once it has had time to finish. */
const SWIPE_SPRING_MS = 200;

/**
 * Should a content node attaching to the swipe ref clear the leftover transform?
 *
 * The reset fires from the content node's callback ref — the one signal that
 * reaches this code on reopen (Radix remounts the node without re-rendering
 * SheetContent; verified in-browser). But that ref is called on EVERY render, not
 * just on remount: React re-attaches it (null → node) whenever the callback churns
 * (StrictMode, an unstable ref), and it also fires the very node while a drag is
 * mid-move. Clearing then would wipe the transform the drag is setting — no swipe.
 *
 * So reset only when a genuinely NEW node attaches (`isNewNode` — this node is not
 * the one we last saw), and never while a dismiss slide is in flight. A reopen
 * always brings a fresh node (Radix unmounts the old content DOM on close); a
 * drag/churn re-attaches the SAME node, so it is left alone. Pure, so the decision
 * is unit-testable without a Radix/DOM lifecycle jsdom cannot model faithfully.
 */
export function shouldClearSwipeOnMount(isNewNode: boolean, dismissInFlight: boolean): boolean {
  return isNewNode && !dismissInFlight;
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = React.useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handle = () => setReduced(mql.matches);
    mql.addEventListener('change', handle);
    return () => mql.removeEventListener('change', handle);
  }, []);
  return reduced;
}

/**
 * Downward drag-to-dismiss for the `side="bottom"` sheet.
 *
 * The tricky part: `SIDE_BOTTOM_CLASSES` puts `overflow-y-auto` on this SAME
 * element — the sheet IS the scroll container the saved-search builder and
 * the filter panel scroll inside. A pointer handler that starts "dragging"
 * on any downward move would hijack that scroll the moment a form taller
 * than the viewport is scrolled from the middle.
 *
 * So the gesture is only ARMED once a pointer has moved past
 * {@link SWIPE_LOCK_THRESHOLD} — until then nothing is touched, so a plain
 * tap still reaches whatever it landed on — and only when, at that moment,
 * the move is downward AND `scrollTop` is already 0. Anything else (moving
 * up, moving mostly sideways, or moving down from mid-scroll) is left
 * entirely to the browser: we never call `preventDefault`, so native
 * scrolling proceeds exactly as if this hook didn't exist. The decision is
 * made once per pointer (down/up/sideways all count as "not a dismiss") —
 * a gesture that starts by scrolling doesn't get a second chance to dismiss
 * later in the same touch.
 *
 * A pointerdown that starts inside a NESTED scroller — the ИКПУ/niche
 * `SubjectPicker` panel, `role="listbox"`, portals into this same element —
 * is excluded up front for the same reason: that panel's own scroll owns
 * the gesture, not the sheet underneath it.
 */
function useSwipeToDismiss(
  enabled: boolean,
  onDismiss: () => void,
  reducedMotion: boolean,
): {
  /** Callback ref for the content node — stores it AND clears any leftover swipe
   *  offset on (re)mount, which is the only reopen signal SheetContent gets. */
  setNode: (node: SheetContentEl | null) => void;
  handlers: Pick<
    React.DOMAttributes<SheetContentEl>,
    'onPointerDown' | 'onPointerMove' | 'onPointerUp' | 'onPointerCancel'
  >;
  style: React.CSSProperties | undefined;
  /** True while a swipe-dismiss is playing its own slide-out — the caller uses
   *  it to suppress Radix's exit keyframe so the two don't fight (the flicker). */
  exiting: boolean;
} {
  const elRef = React.useRef<SheetContentEl | null>(null);
  const [transform, setTransform] = React.useState(0);
  const [springing, setSpringing] = React.useState(false);
  const [exiting, setExiting] = React.useState(false);
  // True only while a swipe-dismiss slide-out is playing (set at release, cleared
  // when the deferred close fires). Lets the reopen-reset tell an in-progress slide
  // apart from a genuine reopen — see `shouldClearSwipeOnOpen`.
  const dismissInFlightRef = React.useRef(false);
  type GestureMode = 'undecided' | 'dragging' | 'scrolling';
  const gesture = React.useRef<{
    pointerId: number;
    startY: number;
    startX: number;
    startTime: number;
    mode: GestureMode;
  } | null>(null);

  const onPointerDown = React.useCallback(
    (e: React.PointerEvent<SheetContentEl>) => {
      if (!enabled) return;
      const target = e.target as HTMLElement;
      if (target.closest('[role="listbox"]')) return;
      gesture.current = {
        pointerId: e.pointerId,
        startY: e.clientY,
        startX: e.clientX,
        startTime: Date.now(),
        mode: 'undecided',
      };
    },
    [enabled],
  );

  const onPointerMove = React.useCallback((e: React.PointerEvent<SheetContentEl>) => {
    const g = gesture.current;
    if (!g || g.pointerId !== e.pointerId || g.mode === 'scrolling') return;
    const el = elRef.current;
    if (!el) return;
    const dy = e.clientY - g.startY;
    const dx = e.clientX - g.startX;

    if (g.mode === 'undecided') {
      if (Math.abs(dy) < SWIPE_LOCK_THRESHOLD && Math.abs(dx) < SWIPE_LOCK_THRESHOLD) return;
      const downward = dy > 0 && Math.abs(dy) > Math.abs(dx);
      if (downward && el.scrollTop <= 0) {
        g.mode = 'dragging';
        el.setPointerCapture?.(e.pointerId);
      } else {
        g.mode = 'scrolling';
        return;
      }
    }

    e.preventDefault();
    setTransform(Math.max(0, dy));
  }, []);

  const finish = React.useCallback(
    (e: React.PointerEvent<SheetContentEl>) => {
      const g = gesture.current;
      gesture.current = null;
      if (!g || g.pointerId !== e.pointerId) return;
      elRef.current?.releasePointerCapture?.(e.pointerId);
      if (g.mode !== 'dragging') return;

      const dy = Math.max(0, e.clientY - g.startY);
      const dt = Math.max(1, Date.now() - g.startTime);
      const velocity = dy / dt;
      if (dy > SWIPE_DISMISS_DISTANCE || velocity > SWIPE_DISMISS_VELOCITY) {
        if (reducedMotion) {
          // No motion preference — close at once, with no slide-out to animate.
          setSpringing(false);
          setTransform(0);
          onDismiss();
          return;
        }
        // Continue the sheet DOWNWARD off-screen from where the finger let go, then
        // unmount. The old code snapped our transform back to translateY(0) and let
        // Radix's `slide-out-to-bottom` keyframe restart the exit from FULL HEIGHT —
        // so the sheet flicked back up to full size for a frame before sliding away.
        // `exiting` suppresses that keyframe (see SheetContent) so only our own
        // slide-out plays; `onDismiss` is deferred until it has.
        const el = elRef.current;
        const distance = el ? el.getBoundingClientRect().height + 40 : window.innerHeight;
        dismissInFlightRef.current = true;
        setExiting(true);
        setSpringing(true); // enables the transform transition below
        setTransform(distance);
        window.setTimeout(() => {
          onDismiss();
          dismissInFlightRef.current = false;
        }, SWIPE_SPRING_MS);
        return;
      }
      // Changed their mind — spring back, unless the user asked for less motion.
      setSpringing(!reducedMotion);
      setTransform(0);
    },
    [onDismiss, reducedMotion],
  );

  React.useEffect(() => {
    if (!springing) return;
    const id = setTimeout(() => setSpringing(false), SWIPE_SPRING_MS);
    return () => clearTimeout(id);
  }, [springing]);

  // Reset any leftover swipe state when the sheet REOPENS.
  //
  // A swipe-dismiss parks the content off-screen (`transform: translateY(height)`)
  // and sets `exiting`, then defers the real close. Because `exiting`'s
  // `animate-none` suppresses the exit keyframe Radix's Presence would unmount on,
  // the SAME content instance survives the close and is reopened WITHOUT a fresh
  // mount — verified in-browser: no unmount happens between the swipe-close and the
  // reopen, the element just flips `data-state` closed→open. So the stale transform
  // leaks in and the reopened panel renders a screen-height below the fold: dark
  // overlay visible, sheet invisible.
  //
  // Reset it on the reopen EDGE — `data-state` going to "open" from a non-open
  // value — never merely because `data-state` IS "open". During our own swipe
  // slide-out the sheet is still `open` the whole time, so a plain "is open" reset
  // would snap the slide back and kill it. A layout effect (no deps, runs after
  // every commit) tracks the previous state; the guard makes it a no-op except on
  // the reopen edge, and `transform !== 0 || exiting` stops any redundant write.
  // Clear a leftover swipe offset when the content node (re)mounts.
  //
  // The reopen is invisible to SheetContent: the sheet's open state lives in
  // Radix's context, consumed by the INNER content, not by SheetContent — so
  // SheetContent does not re-render on reopen (verified in-browser: its effects
  // stop firing after the close). A swipe-dismiss parks the content off-screen
  // (`transform: translateY(height)`) and SheetContent keeps that in state, since
  // Radix only unmounts the content's DOM node, never SheetContent itself. On
  // reopen Radix remounts that node and re-applies SheetContent's STALE style, so
  // the panel comes back a screen-height below the fold — dark overlay visible,
  // sheet gone.
  //
  // The one signal that DOES fire on reopen is this callback ref: Radix attaches
  // it to the freshly-mounted node. So reset here — clear the inline transform
  // imperatively (no flash before the re-render) and sync React state. Skip while
  // a dismiss slide is in flight (the node stays mounted then; the guard just
  // protects against a stray re-attach mid-slide). A no-op when nothing is parked.
  const lastNodeRef = React.useRef<SheetContentEl | null>(null);
  const setNode = React.useCallback((node: SheetContentEl | null) => {
    elRef.current = node;
    if (!node) return; // a detach — nothing to reset, and keep `lastNode` so the
    // churn's immediately-following re-attach of the SAME node is not treated as new.
    const isNewNode = node !== lastNodeRef.current;
    lastNodeRef.current = node;
    if (shouldClearSwipeOnMount(isNewNode, dismissInFlightRef.current)) {
      node.style.transform = '';
      setTransform(0);
      setSpringing(false);
      setExiting(false);
    }
  }, []);

  const style: React.CSSProperties | undefined =
    transform !== 0 || springing
      ? {
          transform: `translateY(${transform}px)`,
          transition: springing ? `transform ${SWIPE_SPRING_MS}ms ease-out` : 'none',
        }
      : undefined;

  return {
    setNode,
    handlers: enabled
      ? { onPointerDown, onPointerMove, onPointerUp: finish, onPointerCancel: finish }
      : {},
    style,
    exiting,
  };
}

/** `a` then `b`, both if both are given — so this hook's own pointer
 *  handlers never silently replace a caller-supplied one. */
function composeHandlers<E>(
  a: ((e: E) => void) | undefined,
  b: ((e: E) => void) | undefined,
): ((e: E) => void) | undefined {
  if (!a) return b;
  if (!b) return a;
  return (e: E) => {
    a(e);
    b(e);
  };
}

const Sheet = DialogPrimitive.Root;

const SheetTrigger = DialogPrimitive.Trigger;

const SheetPortal = DialogPrimitive.Portal;

const SheetClose = DialogPrimitive.Close;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
));
SheetOverlay.displayName = DialogPrimitive.Overlay.displayName;

export type SheetSide = 'right' | 'left' | 'bottom';

interface SheetContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /**
   * When `false`, render WITHOUT the dark backdrop overlay so the rest of
   * the page (sidebar, nav) stays visible and interactive — the panel sits
   * alongside the page rather than over it. Pair with `modal={false}` on
   * the parent `<Sheet>`. Default `true` (modal dialog behavior).
   */
  modal?: boolean;
  /**
   * Anchor edge. Defaults to `'right'` to preserve the existing right-side
   * slide-over behaviour used by the chat panel. `'bottom'` produces a
   * bottom sheet that slides up — used for mobile sheets (see M4 chat
   * refit) and the bottom action sheet pattern.
   */
  side?: SheetSide;
  /**
   * Optional snap points for the bottom-side sheet, expressed as fractions
   * of the viewport height (0–1). When provided, the sheet height is
   * pinned to the first snap point on open. Currently advisory — the
   * actual snap interaction lands with M4; M0 ships the prop surface
   * so call sites can target it without further refactor.
   */
  snapPoints?: ReadonlyArray<number>;
}

const SIDE_RIGHT_CLASSES = cn(
  'fixed right-0 top-0 z-50 h-full w-full sm:max-w-[480px]',
  'bg-cream border-l border-charcoal shadow-xl',
  'duration-300',
  'data-[state=open]:animate-in data-[state=closed]:animate-out',
  'data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right',
  'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
  'flex flex-col',
);

const SIDE_LEFT_CLASSES = cn(
  // Left-anchored off-canvas drawer — used by the mobile navigation. Full
  // height, capped width so page context peeks through the overlay.
  'fixed left-0 top-0 z-50 h-full w-[86vw] max-w-[310px]',
  'bg-bone-surface border-r border-edge shadow-xl',
  'duration-300',
  'data-[state=open]:animate-in data-[state=closed]:animate-out',
  'data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left',
  'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
  'flex flex-col',
);

const SIDE_BOTTOM_CLASSES = cn(
  // Bottom-anchored full-width sheet capped to viewport height.
  'fixed left-0 right-0 bottom-0 z-50 w-full max-h-[92vh] supports-[height:1dvh]:max-h-[92dvh]',
  'bg-cream border-t-[1.5px] border-charcoal rounded-t-[20px] shadow-2xl',
  'duration-200 will-change-transform',
  'data-[state=open]:animate-in data-[state=closed]:animate-out',
  'data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom',
  'flex flex-col',
  // ⚠️ The `max-h-[92…]` above caps the height; without a scroller the overflow
  // is simply UNREACHABLE. A populated saved-search sheet is ~1340px tall against
  // a 746px cap on a 375×812 phone, which put «Сохранить» 600px below the fold
  // with no way to reach it — the form could be filled in and never submitted.
  //
  // ⚠️ And that is why it is spelled TWICE (2026-09-26): `dvh` shipped in Chrome
  // 108, and on Chrome 101 a `dvh` in `max-height` is invalid at computed-value
  // time, so the declaration is dropped and `max-height` computes to `none` —
  // the cap this comment relies on simply did not exist there, and neither did
  // the overflow it makes scrollable. The plain `92vh` is a real cap for those
  // builds; the `supports-[height:1dvh]` twin, emitted inside `@supports` after
  // it at equal specificity, keeps modern browsers on exactly `92dvh`.
  //
  // `SheetBody` (flex-1 + overflow-y-auto) exists for sheets that want a fixed
  // header/footer, but NO consumer used it, so every bottom sheet inherited the
  // bug. Scrolling belongs on the primitive: a sheet that caps its own height
  // owes the user a way to see the rest.
  'overflow-y-auto',
  'pb-[env(safe-area-inset-bottom)]',
);

const SheetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(({ className, children, modal = true, side = 'right', snapPoints, ...props }, ref) => {
  // Compute a starting height for bottom sheets from the first snap point.
  // Heuristic only — full snap behaviour ships in M4.
  const bottomStyle =
    side === 'bottom' && snapPoints && snapPoints.length > 0
      ? { height: `${Math.round(snapPoints[0] * 100)}dvh` }
      : undefined;

  const isBottom = side === 'bottom';
  const reducedMotion = usePrefersReducedMotion();
  // Closes via the SAME path the visible ✕ / overlay tap use (a hidden
  // `DialogPrimitive.Close` click) rather than reaching for `onOpenChange`
  // directly — neither this component nor its two consumers pass one down
  // to `SheetContent`, and Radix's own Close already carries the right
  // context wiring.
  const closeRef = React.useRef<HTMLButtonElement>(null);
  const dismiss = useSwipeToDismiss(isBottom, () => closeRef.current?.click(), reducedMotion);

  const setContentRef = React.useCallback(
    (node: SheetContentEl | null) => {
      dismiss.setNode(node);
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as React.MutableRefObject<SheetContentEl | null>).current = node;
    },
    [ref, dismiss.setNode],
  );

  return (
    <SheetPortal>
      {modal && <SheetOverlay />}
      <DialogPrimitive.Content
        ref={setContentRef}
        className={cn(
          side === 'bottom'
            ? SIDE_BOTTOM_CLASSES
            : side === 'left'
              ? SIDE_LEFT_CLASSES
              : SIDE_RIGHT_CLASSES,
          // During a swipe-dismiss we animate the slide-out ourselves (translateY),
          // so kill Radix's own exit keyframe — otherwise it restarts the exit from
          // translateY(0), snapping the sheet back to full height for a frame.
          dismiss.exiting && 'data-[state=closed]:!animate-none',
          className,
        )}
        style={{ ...bottomStyle, ...dismiss.style }}
        {...props}
        onPointerDown={composeHandlers(props.onPointerDown, dismiss.handlers.onPointerDown)}
        onPointerMove={composeHandlers(props.onPointerMove, dismiss.handlers.onPointerMove)}
        onPointerUp={composeHandlers(props.onPointerUp, dismiss.handlers.onPointerUp)}
        onPointerCancel={composeHandlers(props.onPointerCancel, dismiss.handlers.onPointerCancel)}
      >
        {children}
        {isBottom && (
          <DialogPrimitive.Close
            ref={closeRef}
            tabIndex={-1}
            aria-hidden="true"
            className="hidden"
          />
        )}
      </DialogPrimitive.Content>
    </SheetPortal>
  );
});
SheetContent.displayName = DialogPrimitive.Content.displayName;

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex items-center justify-between px-5 py-4 border-b border-charcoal shrink-0',
      className,
    )}
    {...props}
  />
);
SheetHeader.displayName = 'SheetHeader';

const SheetBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex-1 overflow-y-auto', className)} {...props} />
);
SheetBody.displayName = 'SheetBody';

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 px-5 py-4',
      className,
    )}
    {...props}
  />
);
SheetFooter.displayName = 'SheetFooter';

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-base font-semibold leading-none tracking-tight text-charcoal', className)}
    {...props}
  />
));
SheetTitle.displayName = DialogPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
SheetDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
